import React, { useEffect, useMemo, useRef, useState } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroMosaic.css";

/**
 * HeroMosaic — animation-first family where a curated set of photos
 * (up to 6) tile into a balanced collage on one side of the hero,
 * with the heading / body / CTA stacked on the other side. Three
 * motion siblings share this renderer:
 *
 *   * ``mosaic``         — per-tile drift on a long
 *                          (``mosaic-drift``) cycle. Each tile
 *                          carries a different
 *                          ``--hero-mosaic-tile-index`` so the
 *                          offsets are out of phase and the grid
 *                          breathes without any tile dominating.
 *   * ``mosaic_static``  — no motion. Same layout, the drift class
 *                          is suppressed (``ambient_animation`` is
 *                          ``None`` on the backend so
 *                          ``useHeroAmbient`` returns ""). For users
 *                          whose imagery is busy enough that drift
 *                          would compete with it.
 *   * ``mosaic_shuffle`` — pair-swap rotation. On a slow JS-driven
 *                          interval, two tiles' photos animate out
 *                          (one through the top, one through the
 *                          bottom), then re-enter from the opposite
 *                          edges with their image references swapped
 *                          — so each cycle the photos end up in
 *                          different grid positions than before. The
 *                          grid cells themselves don't move; only
 *                          the *contents* of two cells swap each
 *                          tick. Coordinated like ``spotlight_swap``
 *                          (two elements moving in lockstep can't be
 *                          a single CSS keyframe).
 *
 * Mosaic geometry is driven entirely by image count so the layout
 * always feels intentional regardless of how many photos the user
 * supplies (1 → single full-bleed tile, 2 → side-by-side, 3 → big +
 * two stacked, …, 6 → 3×2 grid). See ``mosaicLayoutFor`` below.
 */

// Mode id mirrored from the backend Mosaic family. Kept as a
// constant so the renderer's branching logic reads as intent
// ("is this the shuffle variant?") rather than an opaque string
// comparison sprinkled through the JSX. The other two siblings
// (``mosaic`` for drift, ``mosaic_static`` for no motion) share the
// same render path — only shuffle needs JS-coordinated state — so
// they don't earn their own constants here.
//
// We canonicalise the active id by stripping a trailing
// ``_right`` suffix FIRST, then a trailing ``_flush`` suffix.
// This lets the renderer treat the three orthogonal axes that
// the Mosaic family expresses through the id (motion / chrome /
// layout) as independent boolean flags:
//
//   * Motion sibling — read off the canonical id (``mosaic`` /
//     ``mosaic_static`` / ``mosaic_shuffle``) after both suffixes
//     have been stripped. Only ``mosaic_shuffle`` needs the JS-
//     coordinated state (``isShuffleVariant`` below).
//   * Chrome (flush) — toggled by ``isFlush`` further down,
//     which checks for a trailing ``_flush`` AFTER stripping
//     ``_right``. This means a combined id like
//     ``mosaic_static_flush_right`` correctly flags both flush
//     chrome AND the row-reversal layout flip.
//   * Layout (right) — toggled by ``isRightAnchored`` further
//     down, which checks for a trailing ``_right`` directly.
//
// Suffix order convention (mirrored from the backend ids):
// ``_right`` always trails any other suffix, so canonicalisation
// strips it first. If the convention ever flips and ``_flush``
// trails ``_right``, both this canonicaliser and the ``isFlush``
// detector below would need updating in lockstep.
//
// Note: the canonical ``mosaic`` (drift) intentionally has NO flush
// sibling — ``mosaic_flush`` was retired because drift motion broke
// the flush finish's "single continuous photographic surface"
// reading. The strip-suffix canonicalisation is harmless for any
// inbound legacy ``mosaic_flush`` ids that slip past the migration
// (they'd canonicalise to ``mosaic`` and pick up the drift branch),
// but the dispatch in ``HeroAutomatic`` no longer routes that id
// here in the first place.
const MODE_SHUFFLE = "mosaic_shuffle";

function stripRightSuffix(modeId) {
  if (typeof modeId !== "string") return modeId;
  return modeId.endsWith("_right")
    ? modeId.slice(0, -"_right".length)
    : modeId;
}

function stripFlushSuffix(modeId) {
  if (typeof modeId !== "string") return modeId;
  return modeId.endsWith("_flush")
    ? modeId.slice(0, -"_flush".length)
    : modeId;
}

function canonicaliseMosaicMode(modeId) {
  return stripFlushSuffix(stripRightSuffix(modeId));
}

// Right-anchored layout mirror detector. Used by the renderer
// below to flip the row direction (``flex-row-reverse`` desktop /
// ``flex-col-reverse`` mobile) so the collage column moves to the
// right and the text column to the left. Backend ``_right``
// siblings (``mosaic_right`` / ``mosaic_static_right`` /
// ``mosaic_static_flush_right`` / ``mosaic_shuffle_right`` /
// ``mosaic_shuffle_flush_right``) all carry a trailing ``_right``
// suffix; checking the suffix directly (not the canonical id)
// keeps this orthogonal to motion / chrome.
function isMosaicRightAnchored(modeId) {
  return typeof modeId === "string" && modeId.endsWith("_right");
}

// Shuffle rotation timings. Tuned so that:
//   * ``CYCLE_MS`` is long enough that each photo gets a chance to
//     sit at its current position before the next swap (matches
//     SpotlightSwap's 4500ms feel).
//   * ``OUT_MS`` covers the "exiting through the edge" half of the
//     swap; ``IN_MS`` covers the re-entry from the opposite edge.
//     Their sum stays comfortably under ``CYCLE_MS`` so the swap
//     completes before the next tick fires.
//   * The CSS transition duration in ``heroMosaic.css`` must match
//     ``OUT_MS`` / ``IN_MS`` for the tile content to settle exactly
//     when the JS state advances.
const SHUFFLE_CYCLE_MS = 4500;
const SHUFFLE_OUT_MS = 600;
const SHUFFLE_IN_MS = 600;

// Below this image count the shuffle has nothing meaningful to
// rotate — with 1 photo there's no pair to swap at all, and with 2
// every cycle just bounces the same two between the same two cells
// (which reads as flicker rather than rotation). Same threshold
// SpotlightSwap uses for its rotation.
const MIN_FOR_SHUFFLE = 2;

const BP_CONFIG = {
  extra_large: { horizontal: true, mediaPct: "55%", contentPx: 64, gap: 8 },
  large:       { horizontal: true, mediaPct: "55%", contentPx: 56, gap: 8 },
  medium:      { horizontal: true, mediaPct: "50%", contentPx: 40, gap: 6 },
  small:       { horizontal: false, contentPx: 24, contentPy: 32, gap: 6, mediaMaxH: "40vh" },
  extra_small: { horizontal: false, contentPx: 16, contentPy: 24, gap: 4, mediaMaxH: "35vh" },
};

// ---------------------------------------------------------------------------
// Layout templates (CSS grid track strings keyed by image count)
// ---------------------------------------------------------------------------
//
// Each entry returns { columns, rows, areas } that get plugged into a
// CSS grid. ``areas`` is a list of the named tile slots (in render
// order) so the renderer can map images → grid-area names without
// caring about how many cells they occupy.

function mosaicLayoutFor(count) {
  switch (count) {
    case 1:
      return {
        columns: "1fr",
        rows: "1fr",
        areas: ['"a"'].join(" "),
        tiles: ["a"],
      };
    case 2:
      return {
        columns: "1fr 1fr",
        rows: "1fr",
        areas: ['"a b"'].join(" "),
        tiles: ["a", "b"],
      };
    case 3:
      // Big tile on the left, two stacked on the right.
      return {
        columns: "1.4fr 1fr",
        rows: "1fr 1fr",
        areas: ['"a b"', '"a c"'].join(" "),
        tiles: ["a", "b", "c"],
      };
    case 4:
      // 2×2 grid — clean and balanced.
      return {
        columns: "1fr 1fr",
        rows: "1fr 1fr",
        areas: ['"a b"', '"c d"'].join(" "),
        tiles: ["a", "b", "c", "d"],
      };
    case 5:
      // One wide tile on top, two pairs beneath — five cells with
      // a hierarchical "feature first" reading order.
      return {
        columns: "1fr 1fr",
        rows: "1.2fr 1fr 1fr",
        areas: ['"a a"', '"b c"', '"d e"'].join(" "),
        tiles: ["a", "b", "c", "d", "e"],
      };
    case 6:
    default:
      return {
        columns: "1fr 1fr 1fr",
        rows: "1fr 1fr",
        areas: ['"a b c"', '"d e f"'].join(" "),
        tiles: ["a", "b", "c", "d", "e", "f"],
      };
  }
}

// ---------------------------------------------------------------------------
// useMosaicShuffle (internal)
// ---------------------------------------------------------------------------
//
// JS-driven pair-swap rotation for the ``mosaic_shuffle`` mode.
//
// State shape:
//   * ``permutation`` — an array of length ``count`` mapping grid
//     slot index → image index. Updated *between* the "out" and "in"
//     phases of each swap, so the re-entering tile content reflects
//     the new image while the CSS slide-in animation plays.
//   * ``swap`` — ``{ a, b, phase }`` describing the active swap, or
//     ``null`` when no swap is in flight. ``phase`` is "out" while
//     the two tiles are exiting, "in" while they're sliding back in
//     from the opposite edges. The renderer reads ``swap`` to pick
//     per-tile CSS classes:
//       - ``a`` exits via the top, re-enters from the bottom.
//       - ``b`` exits via the bottom, re-enters from the top.
//     The two opposite directions are what gives the swap its "the
//     two tiles trade places" reading; if both exited the same way
//     it'd look like a one-way scroll instead.
//
// Pause conditions (mirroring SpotlightSwap's playbook):
//   * ``paused`` (caller) — e.g. mode isn't shuffle, image count
//     below the floor.
//   * ``prefers-reduced-motion`` — never animate for users who asked
//     the OS to suppress motion. Returns the identity permutation so
//     the grid renders normally.
//   * Tab hidden — stop the interval while ``document.hidden`` so
//     background tabs don't burn cycles and users don't return to a
//     surprise mid-swap state.
function useMosaicShuffle(count, { paused = false } = {}) {
  const [permutation, setPermutation] = useState(() =>
    Array.from({ length: count }, (_, i) => i),
  );
  const [swap, setSwap] = useState(null);
  // ``nextPair`` tracks which slot pair to swap next. We rotate the
  // starting index forward each tick so over ``count`` cycles every
  // adjacent pair (and therefore every photo) ends up swapping at
  // least once. Using a ref instead of state keeps the rotation
  // counter from forcing extra re-renders.
  const nextPairRef = useRef(0);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
    );
  }, []);

  // Reset the permutation whenever ``count`` changes (e.g. user
  // added / removed an image in edit mode). Without this the
  // permutation would either contain out-of-range indices (count
  // shrunk) or skip the new images entirely (count grew).
  useEffect(() => {
    setPermutation(Array.from({ length: count }, (_, i) => i));
    setSwap(null);
    nextPairRef.current = 0;
  }, [count]);

  useEffect(() => {
    if (paused || prefersReducedMotion || count < MIN_FOR_SHUFFLE) return;

    let cancelled = false;
    let intervalId = null;
    let outTimer = null;
    let inTimer = null;

    const tick = () => {
      if (cancelled) return;
      // Pick (a, b) such that they're always different. Rotating
      // ``a`` forward each tick means every slot gets a turn at
      // being the "top exit"; ``b = (a + 1) % count`` keeps the
      // pair adjacent in source order, which makes the visual
      // motion feel intentional rather than random (random pairs
      // tend to read as glitches at this cadence).
      const a = nextPairRef.current % count;
      const b = (a + 1) % count;
      nextPairRef.current = (a + 1) % count;

      setSwap({ a, b, phase: "out" });

      outTimer = setTimeout(() => {
        if (cancelled) return;
        setPermutation((prev) => {
          const next = [...prev];
          [next[a], next[b]] = [next[b], next[a]];
          return next;
        });
        setSwap({ a, b, phase: "in" });

        inTimer = setTimeout(() => {
          if (cancelled) return;
          setSwap(null);
        }, SHUFFLE_IN_MS);
      }, SHUFFLE_OUT_MS);
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(tick, SHUFFLE_CYCLE_MS);
    };
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (outTimer) {
        clearTimeout(outTimer);
        outTimer = null;
      }
      if (inTimer) {
        clearTimeout(inTimer);
        inTimer = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    if (typeof document !== "undefined" && !document.hidden) {
      start();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
    };
  }, [count, paused, prefersReducedMotion]);

  return { permutation, swap };
}

// ---------------------------------------------------------------------------
// Mosaic grid (internal)
// ---------------------------------------------------------------------------
//
// Renders the cell layout (``mosaicLayoutFor(count)``) and per-tile
// content. Two motion modes are handled here:
//
//   * Drift (``mosaic`` and reduced-motion fallback) — the per-tile
//     class comes from ``ambientClass`` and is applied to the *outer*
//     tile element so the whole cell breathes. ``mosaic_static``
//     funnels through this path with ``ambientClass = ""``.
//   * Shuffle (``mosaic_shuffle``) — the cell positions are stable;
//     only the inner ``__inner`` wrapper around the ``<img>`` slides
//     up / down. The wrapper's class is derived from ``swap`` (which
//     pair is currently swapping and which phase). ``permutation``
//     selects which photo this slot currently shows so the React
//     re-render after the "out" phase puts the swapped photo into
//     position before the "in" slide plays.

function MosaicGrid({ images, ambientClass, gap, isShuffle, isFlush, permutation, swap, heroItem }) {
  // Cap at 6: backend ``MosaicMode._MOSAIC_MAX = 6``. We re-cap defensively
  // so a payload drift (e.g. a stale slot with 7+ refs) doesn't blow the
  // layout up — extra images are just dropped from the render.
  const used = images.slice(0, 6);
  const layout = mosaicLayoutFor(used.length);

  if (!used.length) return null;

  return (
    <div
      className="hero-mosaic-grid"
      style={{
        gridTemplateColumns: layout.columns,
        gridTemplateRows: layout.rows,
        gridTemplateAreas: layout.areas,
        gap: `${gap}px`,
      }}
    >
      {used.map((_unused, slotIndex) => {
        const tileName = layout.tiles[slotIndex];
        // In shuffle mode, ``permutation[slotIndex]`` selects which
        // photo currently sits in this slot. In drift / static the
        // permutation is the identity so it collapses to ``slotIndex``
        // — same render shape, no special-case JSX.
        const photoIndex =
          permutation && permutation[slotIndex] !== undefined
            ? permutation[slotIndex]
            : slotIndex;
        const img = used[photoIndex] || used[slotIndex];
        // ImageSerializer exposes ``image_url`` (raw) and
        // ``cropped_image_url`` (respects the user's crop). Prefer the
        // cropped variant so the mosaic shows what the user edited.
        const src = img.cropped_image_url || img.image_url;

        // Per-slot shuffle class. ``a`` exits up + re-enters from
        // bottom; ``b`` exits down + re-enters from top. Outside the
        // active swap the wrapper carries no phase class and sits at
        // its resting transform.
        let shuffleClass = "";
        if (isShuffle && swap) {
          if (slotIndex === swap.a) {
            shuffleClass =
              swap.phase === "out"
                ? "hero-mosaic-tile__inner--out-up"
                : "hero-mosaic-tile__inner--in-from-bottom";
          } else if (slotIndex === swap.b) {
            shuffleClass =
              swap.phase === "out"
                ? "hero-mosaic-tile__inner--out-down"
                : "hero-mosaic-tile__inner--in-from-top";
          }
        }

        // Drift class only applies to non-shuffle variants — in
        // shuffle the per-tile drift would compete with the slide
        // motion. ``mosaic_static`` funnels through with ``ambientClass``
        // already empty (backend ``ambient_animation = None``).
        const driftClass = !isShuffle ? ambientClass : "";

        return (
          <div
            key={`mosaic-tile-${slotIndex}`}
            className={`hero-mosaic-tile relative ${isFlush ? "hero-mosaic-tile--flush" : ""} ${driftClass}`.trim()}
            style={{
              gridArea: tileName,
              "--hero-mosaic-tile-index": slotIndex,
            }}
          >
            <div className={`hero-mosaic-tile__inner ${shuffleClass}`.trim()}>
              {src ? (
                (() => {
                  const { className: filterClass, style: filterStyle } =
                    getHeroImageStyle(img);
                  return (
                    <ProgressiveHeroImage
                      src={src}
                      alt={img.alt_text || ""}
                      loading="lazy"
                      draggable={false}
                      className={filterClass}
                      style={filterStyle}
                    />
                  );
                })()
              ) : (
                <div className="hero-mosaic-tile__empty" />
              )}
            </div>
            <EditableSlotMediaOverlay slotData={img} heroItem={heroItem} />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroMosaic main renderer
// ---------------------------------------------------------------------------

const HeroMosaic = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  // ``max_count > 1`` slots serialize as an array; downgrade other
  // shapes defensively so the renderer only ever iterates a flat list.
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image"),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = config.horizontal && hasImages;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Mosaic's canonical ambient motion is per-tile drift driven by
  // the ``hero-ambient-mosaic-drift`` class. The class itself is
  // applied by ``MosaicGrid`` to each tile (never to the whole hero)
  // — we only need ``useHeroAmbient`` here to honour
  // ``prefers-reduced-motion``, which returns "" and zeros out the
  // animation for those users. ``mosaic_static`` and ``mosaic_shuffle``
  // funnel through this same call: static surfaces ``""`` because
  // ``ambient_animation = None`` on the backend; shuffle surfaces
  // ``"hero-ambient-mosaic-shuffle"`` but the renderer doesn't apply
  // it as a per-tile drift (the JS-driven slide takes over instead).
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  // Family branching. Drift / static both render the same DOM with
  // ``ambientClass`` toggling on / off; shuffle rotates which photo
  // sits in each grid slot via JS state. Canonicalise the id so the
  // ``_flush`` variants resolve to their motion sibling for this
  // routing (see ``canonicaliseMosaicMode`` above).
  const canonicalModeId = canonicaliseMosaicMode(item?.hero_mode);
  const isShuffleVariant = canonicalModeId === MODE_SHUFFLE;
  // ``_flush`` variants share this renderer with their canonical
  // sibling (see dispatch comment in ``HeroAutomatic``). The mode
  // id is the only place we can read which finish the user picked
  // — slot contract / locks / overrides are inherited unchanged.
  // The flush flag flips per-tile chrome (corner radius / shadow
  // / border via the ``--flush`` modifier) AND zeros the
  // inter-tile gap and the column's outer padding so the tiles
  // read as one continuous photographic surface butting straight
  // against the column edges.
  // Strip a trailing ``_right`` (layout mirror) BEFORE checking
  // for ``_flush`` (chrome) so combined ids like
  // ``mosaic_static_flush_right`` correctly flag both axes —
  // ``isFlush`` here, ``isRightAnchored`` immediately below.
  // Suffix-order convention: ``_right`` always trails any other
  // suffix on the backend ids, so it's safe to strip first. See
  // the ``canonicaliseMosaicMode`` rationale block at the top of
  // this file for why the two suffix axes are kept independent.
  const isFlush = stripRightSuffix(item?.hero_mode).endsWith("_flush");
  // Right-anchored mirror — flip the row direction so the
  // collage column sits on the right and the text column on the
  // left. The DOM source order (collage first, then text) is
  // unchanged; reversing the flex direction inverts the visual
  // order and, on mobile (column flow), inverts the stack so
  // text reads above the collage. See §13b in the backend
  // ``definitions.py`` for the design rationale.
  const isRightAnchored = isMosaicRightAnchored(item?.hero_mode);
  const { permutation, swap } = useMosaicShuffle(images.length, {
    paused: !isShuffleVariant,
  });

  // Vertical break-out for the mosaic column. Without this, the
  // collage sits inside the hero root's padding box and shows a band
  // of background above and below the tiles — see
  // ``useHeroVerticalPadding`` for the full rationale (same pattern
  // HeroSplit's media column and HeroGallery's image track use). Most
  // visible on the ``*_flush`` variants, where the tiles are supposed
  // to butt straight against the hero's top / bottom edges as one
  // continuous photographic surface; without the breakout the user's
  // configured top / bottom hero padding leaks through as a visible
  // strip above and below the flush tiles. Non-flush variants benefit
  // too: the column's own ``p-3 sm:p-4`` keeps the rounded tiles
  // comfortably inset, so the breakout just removes redundant
  // double-padding (hero pad + column pad) without changing how the
  // tiles read.
  //
  // Only applied on the horizontal (desktop) layout — on mobile the
  // collage stacks on top of the text inside the hero's padded
  // column, and respecting the top / bottom padding is the right
  // visual call there (the tiles aren't supposed to butt against
  // the hero edge when text sits beneath them in the same padded
  // column).
  const heroPad = useHeroVerticalPadding(heroBase.heroRootRef);
  const mosaicBreakoutStyle = isHorizontal
    ? {
        marginTop: heroPad.top ? `-${heroPad.top}px` : 0,
        marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
      }
    : null;

  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // No images filled → behave like Split-with-no-media: stack the text
  // centred so the editor still has something readable while the user
  // is wiring up images.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${
          isHorizontal
            ? isRightAnchored
              ? "flex flex-row-reverse"
              : "flex flex-row"
            : isRightAnchored
              ? "flex flex-col-reverse"
              : "flex flex-col"
        } w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Mosaic column. Uses the same ``min-height: 0`` + absolute-
            wrapper trick HeroGallery uses (see comments there): the
            grid inside is content-sized, so without these guards the
            column would push the row taller than the hero's intended
            ``minHeight: baseHeightPx``. */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={
            isHorizontal
              ? { width: config.mediaPct, minHeight: 0, ...(mosaicBreakoutStyle || {}) }
              : {
                  width: "100%",
                  minHeight: config.mediaMaxH,
                  maxHeight: config.mediaMaxH,
                }
          }
        >
          <div className={`absolute inset-0 ${isFlush ? "p-0" : "p-3 sm:p-4"}`}>
            <MosaicGrid
              images={images}
              ambientClass={ambientClass}
              gap={isFlush ? 0 : config.gap}
              isShuffle={isShuffleVariant}
              isFlush={isFlush}
              permutation={permutation}
              swap={swap}
              heroItem={item}
            />
            <SlotCapacityAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              currentCount={images.length}
              className="absolute inset-x-4 bottom-4"
            />
          </div>
        </div>

        {/* Text column. Same Split-style sizing: ``flex-1`` for the
            allocation, ``min-w-0`` so the column doesn't blow past
            the visible hero when a child has a wide intrinsic size. */}
        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
          style={{
            ...(isHorizontal
              ? { paddingLeft: config.contentPx, paddingRight: config.contentPx }
              : { paddingLeft: config.contentPx, paddingRight: config.contentPx, paddingTop: config.contentPy, paddingBottom: config.contentPy }),
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroMosaic);
